import React, { useCallback, useState } from 'react';

import { Icon } from '@column/column-ui-kit';
import {
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@column/ui';

import { ROUTE } from '~/app/routes';
import { useNavigate } from '~/lib/navigation';
import { ROUTE as PUBLIC_ROUTE } from '~/public/routes';
import { useSessionStore } from '~/stores/Session';

export const UserDropdown: React.FC = () => {
  const { currentUser, settings, changeTheme } = useSessionStore();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [userAnimate, setUserAnimate] = useState(false);
  const [modeAnimate, setModeAnimate] = useState(false);
  const [logoutAnimate, setLogoutAnimate] = useState(false);

  const initials =
    currentUser?.firstName && currentUser?.lastName
      ? `${currentUser.firstName.toString().trim()[0]}${currentUser.lastName.toString().trim()[0]}`
      : '-';

  const handleLogout = useCallback(() => {
    navigate(PUBLIC_ROUTE.LOGIN, { state: { action: 'signOut' } });
  }, []);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Open user menu"
          className="group relative flex h-9 cursor-pointer items-center rounded-lg p-1 text-sm outline-none transition-colors duration-150 ease-out hover:bg-gray-100 data-[state=open]:bg-gray-100 dark:hover:bg-white/[0.08] dark:data-[state=open]:bg-white/[0.08] before:absolute before:inset-x-[-2px] before:inset-y-[-2px] before:content-['']"
        >
          <Avatar className="size-7 rounded-full">
            <AvatarFallback className="rounded-full bg-gray-100 text-[11px] font-medium uppercase shadow-[inset_0_0_0_1px_rgb(0_0_0/0.08)] dark:bg-white/10 dark:shadow-[inset_0_0_0_1px_rgb(255_255_255/0.1)]">
              {initials}
            </AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="min-w-56 rounded-lg" align="end" sideOffset={4}>
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
            <Avatar className="size-8 rounded-full">
              <AvatarFallback className="rounded-full text-xs font-medium uppercase shadow-[inset_0_0_0_1px_rgb(0_0_0/0.08)] dark:shadow-[inset_0_0_0_1px_rgb(255_255_255/0.1)]">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="grid flex-1 gap-px text-left text-sm leading-tight">
              <span className="truncate font-medium">
                {currentUser?.firstName} {currentUser?.lastName}
              </span>
              <span className="truncate text-xs font-normal text-muted-foreground">{currentUser?.email}</span>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => navigate(ROUTE.PROFILE)}
          onMouseEnter={() => !userAnimate && setUserAnimate(true)}
        >
          <Icon.AnimationUser running={userAnimate} onDone={() => setUserAnimate(false)} />
          Profile
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => changeTheme()} onMouseEnter={() => !modeAnimate && setModeAnimate(true)}>
          <Icon.AnimationMode running={modeAnimate} onDone={() => setModeAnimate(false)} />
          {settings.themeType === 'dark' ? 'Lights on' : 'Lights off'}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} onMouseEnter={() => !logoutAnimate && setLogoutAnimate(true)}>
          <Icon.AnimationLogout running={logoutAnimate} onDone={() => setLogoutAnimate(false)} />
          Logout
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
